import React, { useEffect, useState, useCallback } from "react";
import api, { fmtDate } from "../lib/api";
import { Bell, Check, CheckCheck, Info, AlertTriangle, XCircle, CheckCircle2 } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Button } from "./ui/button";
import { useNavigate } from "react-router-dom";

const iconFor = (sev) => {
    switch (sev) {
        case "success": return <CheckCircle2 size={14} className="text-emerald-600" />;
        case "warning": return <AlertTriangle size={14} className="text-amber-600" />;
        case "error": return <XCircle size={14} className="text-rose-600" />;
        default: return <Info size={14} className="text-blue-600" />;
    }
};

export default function NotificationBell() {
    const [count, setCount] = useState(0);
    const [items, setItems] = useState([]);
    const [open, setOpen] = useState(false);
    const nav = useNavigate();

    const refresh = useCallback(async () => {
        try {
            const [c, l] = await Promise.all([
                api.get("/notifications/unread-count"),
                api.get("/notifications"),
            ]);
            setCount(c.data.count);
            setItems(l.data);
        } catch {}
    }, []);

    useEffect(() => {
        refresh();
        const t = setInterval(refresh, 20000);
        return () => clearInterval(t);
    }, [refresh]);

    const markRead = async (id) => {
        try { await api.post(`/notifications/${id}/read`); refresh(); } catch {}
    };
    const markAll = async () => {
        try { await api.post("/notifications/read-all"); refresh(); } catch {}
    };

    const clickItem = (n) => {
        markRead(n.id);
        if (n.link) nav(n.link);
        setOpen(false);
    };

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <button
                    data-testid="notif-bell"
                    className="relative p-2 rounded-md hover:bg-muted transition-colors"
                    aria-label="Bildirimler"
                >
                    <Bell size={18} />
                    {count > 0 && (
                        <span data-testid="notif-count" className="absolute -top-0.5 -right-0.5 w-4 h-4 text-[10px] rounded-full bg-rose-500 text-white flex items-center justify-center font-medium">
                            {count > 9 ? "9+" : count}
                        </span>
                    )}
                </button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-96 p-0">
                <div className="p-3 border-b border-border flex items-center justify-between">
                    <div>
                        <div className="font-semibold text-sm">Bildirimler</div>
                        <div className="text-xs text-muted-foreground">{count} okunmadı</div>
                    </div>
                    {count > 0 && (
                        <Button data-testid="notif-mark-all" variant="ghost" size="sm" onClick={markAll} className="h-7 text-xs gap-1">
                            <CheckCheck size={13} /> Hepsini okundu işaretle
                        </Button>
                    )}
                </div>
                <div className="max-h-[420px] overflow-y-auto">
                    {items.length === 0 ? (
                        <div className="py-10 text-center text-sm text-muted-foreground">
                            <Bell size={22} className="mx-auto mb-2 opacity-40" />
                            Bildirim yok
                        </div>
                    ) : (
                        items.map((n) => (
                            <button
                                key={n.id}
                                data-testid={`notif-item-${n.id}`}
                                onClick={() => clickItem(n)}
                                className={`w-full text-left px-3 py-3 flex gap-3 border-b border-border hover:bg-muted transition-colors ${!n.read ? "bg-blue-50/40 dark:bg-blue-950/10" : ""}`}
                            >
                                <div className="mt-0.5 shrink-0">{iconFor(n.severity)}</div>
                                <div className="flex-1 min-w-0">
                                    <div className="text-sm font-medium truncate">{n.title}</div>
                                    <div className="text-xs text-muted-foreground line-clamp-2">{n.message}</div>
                                    <div className="text-[10px] text-muted-foreground mt-1">{fmtDate(n.created_at)}</div>
                                </div>
                                {!n.read && <span className="w-2 h-2 rounded-full bg-blue-500 mt-2 shrink-0" />}
                            </button>
                        ))
                    )}
                </div>
            </PopoverContent>
        </Popover>
    );
}
